import EndpointManager from '@/endpoints';
import endpoints from '@/endpoints';
import { http } from '@/utils/HttpClient';
import { ApiResponse } from '@/types/api';
import { User } from '@/types/models/user';
import { UpdateRequest } from '@/features/auth/types/http/requests/updateRequest';
import {
  VerificationCheckData,
  VerificationResendData,
  VerificationResponse,
  VerificationSendData,
  VerificationVerifyData,
} from '@/features/auth/types/http/verificationCode';

export const authService = {
  // register the user in the backend
  async register({
    uid,
    email,
    password,
    idToken,
    isSilent,
    displayName,
  }: {
    uid: string;
    email: string;
    password?: string;
    idToken: string;
    isSilent?: boolean;
    displayName?: string;
  }): Promise<ApiResponse<User>> {
    const payload = {
      uid: uid,
      email: email,
      password: password,
      idToken: idToken,
      isSilent: isSilent,
      displayName: displayName,
    };
    return await http.fetch({
      method: EndpointManager.auth.register.method,
      endpoint: EndpointManager.auth.register.url(),
      config: {
        body: payload,
        skipAuth: true,
      },
    });
  },

  // login the user in the backend
  async login(uid: String, idToken: string): Promise<ApiResponse<User>> {
    return await http.fetch({
      method: EndpointManager.auth.login.method,
      endpoint: EndpointManager.auth.login.url(),
      config: {
        body: { uid: uid, idToken: idToken },
        skipAuth: true,
      },
    });
  },

  async logout(uid: string): Promise<ApiResponse<boolean>> {
    return await http.fetch({
      method: endpoints.auth.logout.method,
      endpoint: endpoints.auth.logout.url(),
      config: {
        body: { uid },
      },
    });
  },

  async getCustomToken(uid: string): Promise<ApiResponse<{ token: string }>> {
    return await http.fetch({
      method: endpoints.auth.getCustomToken.method,
      endpoint: endpoints.auth.getCustomToken.url(),
      config: {
        queryParams: { uid },
      },
    });
  },

  // auth the user in the backend
  async authWithProvider(accessToken: string): Promise<ApiResponse<User>> {
    return await http.fetch({
      method: endpoints.auth.authWithProvider.method,
      endpoint: endpoints.auth.authWithProvider.url(),
      config: {
        body: { idToken: accessToken },
        skipAuth: true,
      },
    });
  },

  async updateUser(request: UpdateRequest): Promise<ApiResponse<User>> {
    return await http.fetch({
      method: endpoints.auth.updateUser.method,
      endpoint: endpoints.auth.updateUser.url(),
      config: {
        body: request,
      },
    });
  },
  async deleteUser(request: { uid: string }): Promise<ApiResponse<boolean>> {
    return await http.fetch({
      method: endpoints.auth.deleteUser.method,
      endpoint: endpoints.auth.deleteUser.url(),
      config: {
        body: request,
      },
    });
  },

  async requestEmailChange(email: string): Promise<ApiResponse<any>> {
    return await http.fetch({
      method: endpoints.auth.requestEmailChange.method,
      endpoint: endpoints.auth.requestEmailChange.url(),
      config: {
        body: { newEmail: email },
      },
    });
  },

  async verifyEmailChange(token: string): Promise<ApiResponse<any>> {
    return await http.fetch({
      method: endpoints.auth.verifyEmailChange.method,
      endpoint: endpoints.auth.verifyEmailChange.url(),
      config: {
        queryParams: { token },
      },
    });
  },

  async checkVerificationEmail(
    email: string
  ): Promise<ApiResponse<VerificationResponse<VerificationCheckData>>> {
    return http.fetch({
      method: endpoints.auth.verificationCheck.method,
      endpoint: endpoints.auth.verificationCheck.url(),
      config: {
        body: { email },
        skipAuth: true,
      },
    });
  },

  async sendVerificationCode(
    email: string
  ): Promise<ApiResponse<VerificationResponse<VerificationSendData>>> {
    return http.fetch({
      method: endpoints.auth.verificationSend.method,
      endpoint: endpoints.auth.verificationSend.url(),
      config: {
        body: { email },
        skipAuth: true,
      },
    });
  },

  async sendVerificationLoginCode(
    email: string
  ): Promise<ApiResponse<VerificationResponse<VerificationSendData>>> {
    return http.fetch({
      method: endpoints.auth.verificationLoginSend.method,
      endpoint: endpoints.auth.verificationLoginSend.url(),
      config: {
        body: { email },
        skipAuth: true,
      },
    });
  },

  async verifyCode(
    email: string,
    code: string
  ): Promise<ApiResponse<VerificationResponse<VerificationVerifyData>>> {
    return http.fetch({
      method: endpoints.auth.verificationVerifyCode.method,
      endpoint: endpoints.auth.verificationVerifyCode.url(),
      config: {
        body: { email, code },
        skipAuth: true,
      },
    });
  },

  async resendVerificationCode(
    email: string
  ): Promise<ApiResponse<VerificationResponse<VerificationResendData>>> {
    return http.fetch({
      method: endpoints.auth.verificationResendCode.method,
      endpoint: endpoints.auth.verificationResendCode.url(),
      config: {
        body: { email },
        skipAuth: true,
      },
    });
  },

  async migrateFiles(
    oldUid: string,
    newUid: string
  ): Promise<ApiResponse<any>> {
    return await http.fetch({
      method: EndpointManager.process.migrateFiles.method,
      endpoint: EndpointManager.process.migrateFiles.url(),
      config: {
        body: { oldUid, newUid },
      },
    });
  },
};
